import { useState, useMemo, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn, formatPhoneNumber, getTimeZoneAbbreviation, normalizeSearchStr } from "@/lib/utils";
import { describeSendError } from "@/lib/send-guard";
import { useSendEmailWithPreflight } from "@/hooks/use-pending-email-drafts";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileFilterSheet, MobileFilterField, MobileRegionPicker } from "@/components/mobile/mobile-filter-sheet";
import { useEffectivePermissions } from "@/hooks/use-effective-permissions";
import type { UserProfile } from "@shared/schema";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useResizableColumns } from "@/hooks/use-resizable-columns";
import { useColumnOrder } from "@/hooks/use-column-order";
import { SavedViewsTabStrip } from "@/components/saved-views-tab-strip";
import { CatchMeUpButton } from "@/components/catch-me-up-button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea, VoiceToTextButton } from "@/components/voice-field";
import { Loader2, Pencil, Trash2, Undo2, Redo2, Plus, ArrowUpDown, ArrowUp, ArrowDown, Edit, ChevronDown, Mail, Send, Phone, Bell, Eye, Calendar, Check, ChevronsUpDown, GitMerge, CheckCircle2, ChevronRight, Clock, Users } from "lucide-react";
import { EmptyState } from "@/components/empty-state";
import { ScheduledEmailDialog } from "@/components/scheduled-email-dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogEventDialog } from "@/components/log-event-dialog";
import { TutorialHint } from "@/components/tutorial-hint";
import { RichTextEditor, type RichTextEditorHandle } from "@/components/rich-text-editor";
import { EmailVariablesSelector } from "@/components/email-variables-selector";
import { EmailAttachmentsField, type StagedAttachment } from "@/components/email-attachments-field";
import { CcEmailInput } from "@/components/cc-email-input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";
import { ClientRecordDialog } from "./client-record-dialog";
import { ClientActivityDialog } from "./client-activity-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { Client, Region, ClientTypeRecord, Talent, Invoice } from "@shared/schema";

export const CLIENT_EXPORT_FIELDS = [
  { key: "id", label: "Client ID" },
  { key: "accountName", label: "Account Name" },
  { key: "phoneNumber", label: "Phone Number" },
  { key: "emailAddress", label: "Email Address" },
  { key: "contactName", label: "Contact Name" },
  { key: "address", label: "Address" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "zipCode", label: "Zip Code" },
  // Booking price column. Historically labelled `Amount` and keyed off
  // `amount` — but the clients schema field is `rate`, so the export
  // column was always blank and a re-imported CSV's `Amount` value was
  // dropped by `PATCH /api/bulk/clients` (whose whitelist is `rate`,
  // not `amount`). Aligning the key/label with the schema makes the
  // export populate and the round-trip update work without manual
  // mapping. The shared bulk-actions alias table also reroutes
  // `Amount` / `Rate` / `Event Rate` headers to `rate` for the
  // `clients` entity so older spreadsheets keep auto-mapping.
  { key: "rate", label: "Rate" },
  { key: "dateOfEvent", label: "Date of Event" },
  { key: "eventTime", label: "Event Time" },
  { key: "eventDetails", label: "Booking" },
  { key: "bookingDetails", label: "Booking Details" },
  { key: "notes", label: "Notes" },
  { key: "calendarNotes", label: "Calendar Notes" },
  { key: "dateBooked", label: "Date Booked" },
  { key: "regionName", label: "Region (Name)" },
  { key: "regionId", label: "Region ID" },
  { key: "talentId", label: "Talent ID" },
  { key: "talentName", label: "Talent (Name)" },
  { key: "rep", label: "Rep" },
  { key: "createdBy", label: "Created By" },
];

const SHOW_DETAILS_OPTIONS = [
  "Senior Living Show - 50 Mins",
  "Senior Living Show - 30 Mins",
  "Bag of Tricks Show - 60 Mins",
  "Magic Across America Show",
];

interface LogCallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  onSave: (notes: string) => void;
  isPending: boolean;
}

function LogCallDialog({ open, onOpenChange, title, onSave, isPending }: LogCallDialogProps) {
  const [notes, setNotes] = useState("");

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) setNotes("");
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Record notes from this call.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="call-notes">Call Notes</Label>
            <Textarea
              id="call-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What was discussed?"
              rows={4}
              data-testid="input-call-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} data-testid="button-cancel-log-call">
            Cancel
          </Button>
          <Button
            onClick={() => { if (notes.trim()) onSave(notes); }}
            disabled={!notes.trim() || isPending}
            data-testid="button-save-call"
          >
            {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Call
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ClientsSpreadsheetProps {
  userId: string;
  searchQuery?: string;
  autoOpenClientId?: string;
  initialRegion?: string;
  onAutoOpenComplete?: () => void;
  // Smart-search filter spec (Task #120). Manual toolbar selections
  // take precedence over spec values for the same key.
  smartFilters?: import("@shared/schema").NlFilterSpec | null;
  /** Content rendered to the right of the saved-view tabs (search bar, etc.). */
  savedViewsRightSlot?: React.ReactNode;
}

type UndoAction = 
  | { type: "edit"; clientId: string; field: string; oldValue: any; newValue: any }
  | { type: "delete"; client: Client }
  | { type: "bulkDelete"; clientIds: string[] };

interface RepUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

const getColumns = (clientTypeOptions: string[], hasMultipleTalent: boolean, isAdmin: boolean) => [
  { key: "accountName", label: "Account Name", width: "180px" },
  { key: "phoneNumber", label: "Phone Number", width: "140px", type: "phone" },
  { key: "emailAddress", label: "Email Address", width: "200px" },
  { key: "contactName", label: "Contact Name", width: "160px" },
  { key: "dateOfEvent", label: "Date of Event", width: "140px", type: "date" },
  { key: "amount", label: "Amount", width: "100px", type: "readonly" },
  { key: "eventTime", label: `Event Time (${getTimeZoneAbbreviation()})`, width: "120px" },
  { key: "eventDetails", label: "Booking", width: "280px", type: "eventDetails" },
  { key: "address", label: "Address", width: "200px", type: "address" },
  { key: "city", label: "City", width: "120px" },
  { key: "state", label: "State", width: "80px" },
  { key: "zipCode", label: "Zip", width: "80px" },
  { key: "clientType", label: "Client Type", width: "130px", type: "select", options: clientTypeOptions },
  { key: "dateBooked", label: "Date Booked", width: "140px", type: isAdmin ? "date" : "readonly" },
  { key: "invoiceNumber", label: "Invoice #", width: "120px" },
  { key: "regionId", label: "Region", width: "140px", type: "region" },
  ...(hasMultipleTalent ? [{ key: "talentId", label: "Talent", width: "140px", type: "talent" }] : []),
  { key: "rep", label: "Rep", width: "140px", type: isAdmin ? "user-select" : "readonly" },
];

export function ClientsSpreadsheet({ userId, searchQuery = "", autoOpenClientId, initialRegion, onAutoOpenComplete, smartFilters = null, savedViewsRightSlot }: ClientsSpreadsheetProps) {
  const isMobile = useIsMobile();
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isActivityDialogOpen, setIsActivityDialogOpen] = useState(false);
  const [isMobileActionSheetOpen, setIsMobileActionSheetOpen] = useState(false);
  const [isNewClientDialogOpen, setIsNewClientDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [selectedRegions, setSelectedRegions] = useState<string[]>(() => {
    // If a region was passed in via URL params, use it; otherwise fall back to localStorage
    if (initialRegion) return [initialRegion];
    try {
      const saved = localStorage.getItem("clients-selected-regions");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (e) {
      // Ignore parse errors
    }
    return [];
  });
  const autoOpenHandledRef = useRef(false);
  const clickGuardRef = useRef(false);
  const [selectedClientType, setSelectedClientType] = useState<string>(() => {
    // Load saved client type from localStorage.
    // null means "never been set" → sentinel so default logic can apply DB preference.
    // "" means user explicitly chose "All Client Types".
    const saved = localStorage.getItem("clients-selected-client-type");
    return saved !== null ? saved : "__unset__";
  });
  const [regionSearch, setRegionSearch] = useState("");
  // Task #324 — "__unset__" sentinel resolves to the workspace default talent
  // (or the lone talent in single-talent workspaces) once talent loads. An
  // explicit user choice is tracked so an auto-resolved default is never
  // persisted as if the user picked it.
  const [selectedTalentId, setSelectedTalentId] = useState<string>(() => {
    const saved = localStorage.getItem("clients-selected-talent");
    return saved !== null ? saved : "__unset__";
  });
  const talentPrefExplicitRef = useRef<boolean>(
    localStorage.getItem("clients-selected-talent") !== null,
  );
  const [undoStack, setUndoStack] = useState<UndoAction[]>([]);
  const [redoStack, setRedoStack] = useState<UndoAction[]>([]);
  // Server-driven sort. Initial state matches the legacy UX
  // (dateBooked DESC). When the user clears sort by toggling past
  // "desc", we drop back to `null`/"default" which routes to the
  // backend's default ordering (dateOfEvent DESC).
  const [sortField, setSortField] = useState<string | null>("dateBooked");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const [selectedClientIds, setSelectedClientIds] = useState<Set<string>>(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);
  const [primaryClientId, setPrimaryClientId] = useState<string>("");
  const [mergeStep, setMergeStep] = useState<1 | 2 | 3>(1);
  // For step 2: which value is chosen for each field ("primary" | "secondary" | "custom")
  type MergeFieldKey = "accountName" | "contactName" | "emailAddress" | "phoneNumber" | "mobilePhone" | "address" | "city" | "state" | "zipCode" | "notes";
  const [mergeFieldSelections, setMergeFieldSelections] = useState<Record<MergeFieldKey, "primary" | "secondary">>({
    accountName: "primary", contactName: "primary", emailAddress: "primary",
    phoneNumber: "primary", mobilePhone: "primary", address: "primary",
    city: "primary", state: "primary", zipCode: "primary", notes: "primary",
  });
  const [mergeEditedValues, setMergeEditedValues] = useState<Record<MergeFieldKey, string>>({
    accountName: "", contactName: "", emailAddress: "",
    phoneNumber: "", mobilePhone: "", address: "",
    city: "", state: "", zipCode: "", notes: "",
  });
  const [bulkEditField, setBulkEditField] = useState("");
  const [bulkEditValue, setBulkEditValue] = useState("");
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [isClientEmailScheduleOpen, setIsClientEmailScheduleOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailAttachments, setEmailAttachments] = useState<StagedAttachment[]>([]);
  const [emailRecipient, setEmailRecipient] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [isLogCallDialogOpen, setIsLogCallDialogOpen] = useState(false);
  const [isSetReminderDialogOpen, setIsSetReminderDialogOpen] = useState(false);
  const [reminderDate, setReminderDate] = useState("");
  const [reminderNote, setReminderNote] = useState("");
  const [isLogEventDialogOpen, setIsLogEventDialogOpen] = useState(false);
  // Server-side cursor pagination + virtualization replaces the
  // legacy "visibleClientsCount + slice" model. The IntersectionObserver
  // attached to loadMoreSentinelRef (declared above) drives
  // fetchNextPage; the row virtualizer below handles the DOM cost.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const queryClientHook = useQueryClient();

  const { data: profile } = useQuery<UserProfile>({
    queryKey: ["/api/profile"],
  });
  const isAdmin = profile?.role === "admin";
  const { data: smsStatusData } = useQuery<{ smsEnabled: boolean }>({
    queryKey: ["/api/workspace/sms-status"],
  });
  const twilioEnabled = !!smsStatusData?.smsEnabled;
  // Region editing is admin-only by default, but admins can grant the
  // `clients.feature.editRegion` permission to other roles via the
  // Users & Permissions dialog.
  const { can: canPerm } = useEffectivePermissions();
  // Admin role default for `clients.feature.editRegion` is true in the
  // registry, so admins naturally pass this check. We deliberately do
  // NOT add `isAdmin || ...` here: when an admin is using "View as
  // user" the perms query auto-applies the X-View-As-UserId header, so
  // the UI must reflect the impersonated user's perms to stay in sync
  // with the server (which gates the same way on the PATCH route).
  const canEditRegion = canPerm("clients.feature.editRegion");
  // Bulk Edit / Bulk Delete (and Merge) are gated behind this permission key so
  // admins can authorize specific roles/users via the Users & Permissions UI.
  // Selection itself is open to everyone (see checkbox rendering below).
  const canBulkActions = canPerm("clients.feature.bulkActions");

  // Server-side cursor pagination + sort/filter pushdown. Mirrors the
  // pattern in leads-spreadsheet.tsx — see that file for the
  // architectural reasoning. The list of `serverSort`-able columns is
  // declared further down (SERVER_SORTABLE) so the toggleSort handler
  // can gate on the same set the storage layer enforces.
  // The `clients` value below is the flattened pages[].items array,
  // already filtered + sorted by Postgres.
  const SERVER_SORTABLE = useMemo(
    () =>
      new Set([
        "accountName",
        "contactName",
        "dateOfEvent",
        "dateBooked",
        "clientType",
        "city",
        "rep",
      ]),
    [],
  );
  const serverSort =
    sortField && SERVER_SORTABLE.has(sortField) ? sortField : "default";
  const serverDir: "asc" | "desc" = sortField ? sortDirection : "desc";

  // Debounce search keystrokes so we don't hammer /api/clients on
  // every char. Matches the 200ms cadence used by the leads
  // spreadsheet.
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 200);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const clientsQueryKey = useMemo(
    () =>
      [
        "/api/clients",
        {
          regions: [...selectedRegions].sort().join(","),
          sort: serverSort,
          dir: serverDir,
          q: debouncedSearch.trim() || null,
          clientType:
            selectedClientType && selectedClientType !== "__unset__"
              ? selectedClientType
              : null,
          talentId:
            selectedTalentId && selectedTalentId !== "all" && selectedTalentId !== "__unset__"
              ? selectedTalentId
              : null,
          smart: smartFilters ? JSON.stringify(smartFilters) : null,
        },
      ] as const,
    [
      selectedRegions,
      serverSort,
      serverDir,
      debouncedSearch,
      selectedClientType,
      selectedTalentId,
      smartFilters,
    ],
  );

  const {
    data: clientsPages,
    isLoading,
    isError: clientsIsError,
    refetch: refetchClients,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<{ items: Client[]; nextCursor: string | null; total?: number }>({
    queryKey: clientsQueryKey,
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set("limit", "100");
      params.set("skipTotal", "1");
      if (pageParam) params.set("cursor", pageParam as string);
      params.set("sort", serverSort);
      params.set("dir", serverDir);
      if (selectedRegions.length > 0) {
        params.set("regionIds", selectedRegions.join(","));
      }
      const q = debouncedSearch.trim();
      if (q) params.set("q", q);
      if (selectedClientType && selectedClientType !== "__unset__") {
        params.set("clientType", selectedClientType);
      }
      if (selectedTalentId && selectedTalentId !== "all" && selectedTalentId !== "__unset__") {
        params.set("talentId", selectedTalentId);
      }
      if (smartFilters) {
        if (smartFilters.regionIds && smartFilters.regionIds.length > 0 && selectedRegions.length === 0) {
          params.set("regionIds", smartFilters.regionIds.join(","));
        }
        if (smartFilters.clientType && !params.has("clientType")) {
          params.set("clientType", smartFilters.clientType);
        }
        if (smartFilters.talentId && !params.has("talentId")) {
          params.set("talentId", smartFilters.talentId);
        }
        if (smartFilters.venueType) {
          params.set("venueType", smartFilters.venueType);
        }
        if (smartFilters.dateOfEventFrom) {
          params.set("dateOfEventFrom", smartFilters.dateOfEventFrom);
        }
        if (smartFilters.dateOfEventTo) {
          params.set("dateOfEventTo", smartFilters.dateOfEventTo);
        }
      }
      const response = await fetch(`/api/clients?${params.toString()}`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch clients");
      return response.json();
    },
  });

  const clients = useMemo<Client[]>(
    () => (clientsPages?.pages ?? []).flatMap((p) => p.items),
    [clientsPages],
  );

  useEffect(() => {
    if (clientsIsError) window.dispatchEvent(new CustomEvent("clarik:query-failure"));
  }, [clientsIsError]);

  // Desktop row virtualizer — only the rows in or near the viewport
  // mount into the DOM. Mobile keeps un-virtualized cards because the
  // card heights vary with content.
  const rowVirtualizer = useVirtualizer({
    count: clients.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 28, // h-7 row height
    overscan: 10,
  });

  const { data: regions = [] } = useQuery<Region[]>({
    queryKey: ["/api/regions"],
  });

  const { data: clientTypes = [] } = useQuery<ClientTypeRecord[]>({
    queryKey: ["/api/client-types"],
  });

  const { data: defaultClientType } = useQuery<ClientTypeRecord | null>({
    queryKey: ["/api/client-types/default"],
  });

  const { data: talent = [] } = useQuery<Talent[]>({
    queryKey: ["/api/talent"],
  });

  // Task #324 — resolve the "__unset__" sentinel once talent loads: a lone
  // talent auto-selects, otherwise fall back to the workspace default talent,
  // and finally "all".
  useEffect(() => {
    if (selectedTalentId !== "__unset__") return;
    if (talent.length === 0) return;
    const activeTalent = talent.filter((t) => t.isActive);
    if (activeTalent.length === 1) {
      setSelectedTalentId(activeTalent[0].id);
      return;
    }
    const def = activeTalent.find((t) => t.isDefault) || talent.find((t) => t.isDefault);
    setSelectedTalentId(def ? def.id : "all");
  }, [selectedTalentId, talent]);

  const { data: invalidEmailsData = [] } = useQuery<{ email: string; result: string }[]>({
    queryKey: ["/api/email-verifications"],
    staleTime: 5 * 60 * 1000, // 5 min — doesn't need to be real-time
  });
  const invalidEmailSet = useMemo(
    () => new Set(invalidEmailsData.map((e) => e.email.toLowerCase())),
    [invalidEmailsData],
  );

  const { data: invoicesData = [] } = useQuery<Invoice[]>({
    queryKey: ["/api/invoices"],
    enabled: isMergeDialogOpen,
  });

  const { data: users = [] } = useQuery<RepUser[]>({
    queryKey: ["/api/users"],
    enabled: isAdmin,
  });

  interface EmailTemplate {
    id: string;
    name: string;
    subject: string;
    body: string;
    bodyRich?: string | null;
    hiddenFromPickers?: boolean;
  }

  const { data: emailTemplates = [] } = useQuery<EmailTemplate[]>({
    queryKey: ["/api/email-templates"],
    enabled: isEmailDialogOpen,
  });

  const { data: documentInstances = [] } = useQuery<any[]>({
    queryKey: ["/api/document-instances"],
  });

  // Build a map of clientId -> doc status for indicator (only for requiresDocuments clients)
  const clientDocStatusMap = useMemo(() => {
    const map = new Map<string, "waiting" | "pending" | "completed">();
    const instancesByClient = new Map<string, any[]>();
    for (const inst of documentInstances) {
      if (inst.clientId) {
        if (!instancesByClient.has(inst.clientId)) instancesByClient.set(inst.clientId, []);
        instancesByClient.get(inst.clientId)!.push(inst);
      }
    }
    for (const client of clients) {
      if (!(client as any).requiresDocuments) continue;
      const insts = instancesByClient.get(client.id) || [];
      if (insts.some((i: any) => i.status === "sent")) {
        map.set(client.id, "pending");
      } else if (insts.length > 0 && insts.every((i: any) => i.status === "completed")) {
        map.set(client.id, "completed");
      } else {
        map.set(client.id, "waiting");
      }
    }
    return map;
  }, [documentInstances, clients]);

  const hasMultipleTalent = talent.filter(e => e.isActive).length > 1;

  // Auto-open a client record when navigating in via `?openClient=…`
  // (lead conversion or sidebar quick-find). Reset the one-shot ref
  // whenever the param is absent so subsequent same-page quick-find
  // clicks (open A → close → open B) actually re-fire the effect.
  //
  // We open the dialog immediately rather than waiting for the
  // cursor-paginated list to load and then searching it. The
  // `selectedClient` derivation below fetches the single record via
  // GET /api/clients/:id when it isn't already in the loaded list, so
  // the just-converted client appears near-instantly while the list
  // keeps loading in the background for when the dialog closes.
  useEffect(() => {
    if (!autoOpenClientId) {
      autoOpenHandledRef.current = false;
      return;
    }
    if (autoOpenHandledRef.current) return;
    autoOpenHandledRef.current = true;
    setSelectedClientId(autoOpenClientId);
    setIsDialogOpen(true);
    onAutoOpenComplete?.();
  }, [autoOpenClientId, onAutoOpenComplete]);

  // Per-user client-type restriction. Admin/billing always see everything.
  const userAllowedClientTypeIds: string[] = useMemo(
    () => ((profile as any)?.allowedClientTypeIds as string[] | undefined) || [],
    [profile]
  );
  const userIsRestrictedByType =
    userAllowedClientTypeIds.length > 0 && profile?.role !== "admin" && profile?.role !== "billing";

  // Set default client type if not saved in localStorage, or if saved type is invalid
  useEffect(() => {
    if (clientTypes.length === 0) return;
    const allowedNames = userIsRestrictedByType
      ? new Set(clientTypes.filter(ct => userAllowedClientTypeIds.includes(ct.id)).map(ct => ct.name))
      : null;
    const isAllowedName = (n: string) => !allowedNames || allowedNames.has(n);

    const isAllSelected = selectedClientType === "";
    const isUnset = selectedClientType === "__unset__";
    const currentValid =
      (isAllSelected && !userIsRestrictedByType) ||
      (!isUnset && clientTypes.some(ct => ct.name === selectedClientType && ct.isActive) && isAllowedName(selectedClientType));

    if (currentValid) return;

    if (userIsRestrictedByType) {
      const firstAllowed = clientTypes.find(ct => ct.isActive && userAllowedClientTypeIds.includes(ct.id));
      if (firstAllowed) {
        setSelectedClientType(firstAllowed.name);
        localStorage.setItem("clients-selected-client-type", firstAllowed.name);
      }
      return;
    }

    if (defaultClientType?.name && defaultClientType.isActive) {
      setSelectedClientType(defaultClientType.name);
      localStorage.setItem("clients-selected-client-type", defaultClientType.name);
    } else {
      setSelectedClientType("");
      localStorage.setItem("clients-selected-client-type", "");
    }
  }, [selectedClientType, defaultClientType, clientTypes, userIsRestrictedByType, userAllowedClientTypeIds]);

  const clientTypeOptions = useMemo(() => {
    let list = clientTypes.filter(ct => ct.isActive);
    if (userIsRestrictedByType) {
      list = list.filter(ct => userAllowedClientTypeIds.includes(ct.id));
    }
    return list
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map(ct => ct.name);
  }, [clientTypes, userIsRestrictedByType, userAllowedClientTypeIds]);

  const VISIBLE_COLUMNS = useMemo(() => getColumns(clientTypeOptions, hasMultipleTalent, isAdmin), [clientTypeOptions, hasMultipleTalent, isAdmin]);
  const visibleColumnKeys = useMemo(() => VISIBLE_COLUMNS.map((c) => c.key), [VISIBLE_COLUMNS]);
  const { orderedKeys, moveColumn } = useColumnOrder({
    storageKey: "clients-column-order",
    columnKeys: visibleColumnKeys,
  });
  const COLUMNS = useMemo(() => {
    const map = new Map(VISIBLE_COLUMNS.map((c) => [c.key, c] as const));
    return orderedKeys.map((k) => map.get(k)!).filter(Boolean);
  }, [orderedKeys, VISIBLE_COLUMNS]);
  const [draggedColKey, setDraggedColKey] = useState<string | null>(null);
  const [dragOverColKey, setDragOverColKey] = useState<string | null>(null);

  const { getColumnWidth, handleResizeStart } = useResizableColumns({
    columns: COLUMNS,
    storageKey: "clients-column-widths",
  });

  // Save selected regions to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("clients-selected-regions", JSON.stringify(selectedRegions));
  }, [selectedRegions]);

  // Save selected client type to localStorage when it changes (never save the sentinel)
  useEffect(() => {
    if (selectedClientType !== "__unset__") {
      localStorage.setItem("clients-selected-client-type", selectedClientType);
    }
  }, [selectedClientType]);

  // Save selected talent to localStorage only when it reflects an explicit
  // user choice (never the "__unset__" sentinel or an auto-resolved default).
  useEffect(() => {
    if (selectedTalentId === "__unset__") return;
    if (!talentPrefExplicitRef.current) return;
    localStorage.setItem("clients-selected-talent", selectedTalentId);
  }, [selectedTalentId]);

  // The infinite query refetches from page 1 whenever any of its key
  // dimensions change (sort, dir, regions, clientType, talent,
  // debounced q), so we no longer need a "reset visible count" effect.
  // The IntersectionObserver below drives fetchNextPage when the
  // sentinel scrolls into view.
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1, rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, clients.length, isMobile]);

  // Build name → id map and helper for region narrowing by client type (T105/T106)
  const clientTypeNameToId = useMemo(() => {
    const m = new Map<string, string>();
    for (const ct of clientTypes) m.set(ct.name, ct.id);
    return m;
  }, [clientTypes]);

  const regionsForClientType = (clientTypeName?: string | null): Region[] => {
    if (!clientTypeName) return regions;
    const ctId = clientTypeNameToId.get(clientTypeName);
    if (!ctId) return regions;
    return regions.filter((r) => {
      const ids = (r as any).clientTypeIds as string[] | null | undefined;
      return !ids || ids.length === 0 || ids.includes(ctId);
    });
  };

  const clientTypeFilteredRegions = useMemo(() => {
    if (!selectedClientType || selectedClientType === "__unset__") return regions;
    return regionsForClientType(selectedClientType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regions, selectedClientType, clientTypeNameToId]);

  const filteredRegions = useMemo(() => {
    const base = clientTypeFilteredRegions;
    if (!regionSearch.trim()) return base;
    const search = normalizeSearchStr(regionSearch);
    return base.filter(r => normalizeSearchStr(r.name).includes(search));
  }, [clientTypeFilteredRegions, regionSearch]);

  const updateClientMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Client> }) => {
      return apiRequest("PATCH", `/api/clients/${id}`, updates);
    },
    onSuccess: () => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update client.", variant: "destructive" });
    },
  });

  const deleteClientMutation = useMutation({
    mutationFn: async (client: Client) => {
      const response = await apiRequest("DELETE", `/api/clients/${client.id}`);
      return { ...await response.json(), deletedClient: client };
    },
    onSuccess: (_, deletedClient: Client) => {
      // Add to undo stack after successful deletion using the variable passed to mutate
      setUndoStack(prev => [...prev, { type: "delete", client: deletedClient }]);
      setRedoStack([]);
      queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
      toast({ title: "Client Deleted", description: "The client has been permanently deleted." });
      setClientToDelete(null);
      setSelectedClientId(null);
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete client.", variant: "destructive" });
    },
  });

  const bulkEditMutation = useMutation({
    mutationFn: async ({ ids, field, value }: { ids: string[]; field: string; value: any }) => {
      const updates = ids.map(id => 
        apiRequest("PATCH", `/api/clients/${id}`, { [field]: value })
      );
      return Promise.all(updates);
    },
    onSuccess: () => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
      setSelectedClientIds(new Set());
      setIsBulkEditOpen(false);
      setBulkEditField("");
      setBulkEditValue("");
      toast({ title: "Clients Updated", description: `Successfully updated ${selectedClientIds.size} clients.` });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update clients.", variant: "destructive" });
    },
  });

  const handleBulkEdit = () => {
    if (selectedClientIds.size === 0 || !bulkEditField) return;
    bulkEditMutation.mutate({
      ids: Array.from(selectedClientIds),
      field: bulkEditField,
      value: bulkEditValue,
    });
  };

  const bulkDeleteMutation = useMutation({
    mutationFn: async () => {
      // The filter set MUST mirror what the visible /api/clients query
      // applies — including any active smart-search spec — or the
      // delete count will diverge from the row count the user sees.
      // Manual toolbar selections take precedence over the spec for the
      // same key, mirroring the GET-request merge logic above.
      const manualClientType =
        selectedClientType && selectedClientType !== "__unset__"
          ? selectedClientType
          : null;
      const manualTalentId =
        selectedTalentId && selectedTalentId !== "all" && selectedTalentId !== "__unset__"
          ? selectedTalentId
          : null;
      const manualRegionIds =
        selectedRegions.length > 0 ? selectedRegions : null;
      const body = selectAllMatching
        ? {
            action: "delete" as const,
            scope: {
              type: "filters" as const,
              filters: {
                q: debouncedSearch.trim() || null,
                clientType:
                  manualClientType ?? smartFilters?.clientType ?? null,
                regionIds:
                  manualRegionIds ??
                  (smartFilters?.regionIds && smartFilters.regionIds.length > 0
                    ? smartFilters.regionIds
                    : null),
                talentId: manualTalentId ?? smartFilters?.talentId ?? null,
                venueType: smartFilters?.venueType ?? null,
                dateOfEventFrom: smartFilters?.dateOfEventFrom ?? null,
                dateOfEventTo: smartFilters?.dateOfEventTo ?? null,
              },
            },
          }
        : {
            action: "delete" as const,
            scope: { type: "ids" as const, ids: Array.from(selectedClientIds) },
          };
      // Capture the explicit IDs (when not selecting all matching) so the
      // delete can be pushed onto the undo stack and reversed via the
      // per-record restore endpoint.
      const deletedIds = selectAllMatching ? [] : Array.from(selectedClientIds);
      const response = await apiRequest("POST", "/api/clients/bulk-action", body);
      const json = (await response.json()) as { count: number };
      return { ...json, deletedIds };
    },
    onSuccess: (result) => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
      // Bulk delete is a soft delete; push it onto the undo stack so it can be
      // reversed. We can only restore by explicit IDs, so "select all matching"
      // deletes (no captured IDs) are not undoable.
      if (result.deletedIds.length > 0) {
        setUndoStack(prev => [...prev, { type: "bulkDelete", clientIds: result.deletedIds }]);
        setRedoStack([]);
      }
      setSelectedClientIds(new Set());
      setSelectAllMatching(false);
      setIsBulkDeleteOpen(false);
      toast({ title: "Clients Deleted", description: `Successfully deleted ${result.count} clients. Use Undo to restore.` });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete clients.", variant: "destructive" });
    },
  });

  const handleBulkDelete = () => {
    if (selectedClientIds.size === 0) return;
    bulkDeleteMutation.mutate();
  };

  // Clear "select all matching" whenever the underlying filters change.
  // The smart-search spec is included so a re-parsed sentence also
  // resets the intent — see leads-spreadsheet for the full rationale.
  useEffect(() => {
    setSelectAllMatching(false);
  }, [
    debouncedSearch,
    selectedClientType,
    selectedTalentId,
    selectedRegions,
    smartFilters,
  ]);

  const mergeClientsMutation = useMutation({
    mutationFn: async ({ primaryClientId, secondaryClientId, overrides }: { primaryClientId: string; secondaryClientId: string; overrides?: Record<string, string> }) => {
      const response = await apiRequest("POST", "/api/clients/merge", { primaryClientId, secondaryClientId, overrides });
      return response.json();
    },
    onSuccess: (data: any) => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClientHook.invalidateQueries({ queryKey: ["/api/invoices"] });
      setSelectedClientIds(new Set());
      setIsMergeDialogOpen(false);
      setPrimaryClientId("");
      setMergeStep(1);
      toast({ 
        title: "Clients Merged", 
        description: `Successfully merged clients. ${data.invoicesMoved || 0} invoice(s) transferred.` 
      });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to merge clients.", variant: "destructive" });
    },
  });

  const handleMergeClients = () => {
    if (selectedClientIds.size !== 2 || !primaryClientId) return;
    const ids = Array.from(selectedClientIds);
    const secondaryClientId = ids.find(id => id !== primaryClientId);
    if (!secondaryClientId) return;
    const primaryClient = clients.find(c => c.id === primaryClientId);
    const secondaryClient = clients.find(c => c.id === secondaryClientId);
    if (!primaryClient || !secondaryClient) return;
    // Build overrides from user selections
    const overrides: Record<string, string> = {};
    const mergeFields: MergeFieldKey[] = ["accountName", "contactName", "emailAddress", "phoneNumber", "mobilePhone", "address", "city", "state", "zipCode", "notes"];
    for (const field of mergeFields) {
      const editedVal = mergeEditedValues[field];
      if (editedVal !== undefined && editedVal !== null) {
        // Use the edited value (which was pre-filled from the selection)
        overrides[field] = editedVal;
      }
    }
    mergeClientsMutation.mutate({ primaryClientId, secondaryClientId, overrides });
  };

  const sendEmailViaPreflight = useSendEmailWithPreflight();
  const [sendingEmail, setSendingEmail] = useState(false);

  const sendEmailMutation = {
    isPending: sendingEmail,
    mutate: async ({ to, subject, body, clientId, cc, attachments }: { to: string; subject: string; body: string; clientId?: string; cc?: string; attachments?: StagedAttachment[] }) => {
      // Close the dialog immediately so the user can keep working; the send
      // (or draft hold) happens in the background.
      setIsEmailDialogOpen(false);
      const recipient = to;
      const sub = subject;
      setEmailSubject("");
      setEmailBody("");
      setEmailRecipient("");
      setEmailCc("");
      setEmailAttachments([]);
      setSendingEmail(true);
      await sendEmailViaPreflight(
        {
          endpoint: "/api/email/send",
          payload: {
            to,
            subject,
            body,
            clientId,
            cc: cc || undefined,
            attachments: attachments && attachments.length > 0
              ? attachments.map((a) => ({ filename: a.filename, contentBase64: a.contentBase64, contentType: a.contentType }))
              : undefined,
          },
          recipientEmail: recipient,
          subject: sub,
          description: "client email",
        },
        {
          onSent: () => {
            toast({ title: "Email sent", description: "Your email has been sent successfully." });
          },
          onQueued: () => {
            // Toast handled by hook.
          },
          onError: (error: any) => {
            const { title, description } = describeSendError(error, "Failed to send email.");
            toast({ title, description, variant: "destructive" });
          },
        },
      );
      setSendingEmail(false);
    },
  } as const;

  const logCallMutation = useMutation({
    mutationFn: async ({ clientId, notes }: { clientId: string; notes: string }) => {
      const response = await apiRequest("POST", `/api/clients/${clientId}/activities`, {
        type: "call",
        notes,
      });
      return response.json();
    },
    onSuccess: () => {
      setIsLogCallDialogOpen(false);
      toast({ title: "Call Logged", description: "Your call notes have been saved." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to log call.", variant: "destructive" });
    },
  });

  const setReminderMutation = useMutation({
    mutationFn: async ({ clientId, date, note }: { clientId: string; date: string; note: string }) => {
      const response = await apiRequest("POST", `/api/reminders`, {
        leadId: clientId,
        reminderDate: date,
        note,
      });
      return response.json();
    },
    onSuccess: () => {
      setIsSetReminderDialogOpen(false);
      setReminderDate("");
      setReminderNote("");
      toast({ title: "Reminder Set", description: "Your reminder has been saved." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to set reminder.", variant: "destructive" });
    },
  });

  const handleOpenEmailDialog = (email: string) => {
    setEmailRecipient(email);
    setEmailSubject("");
    setEmailBody("");
    setEmailCc("");
    setEmailAttachments([]);
    setSelectedTemplateId("");
    setIsEmailDialogOpen(true);
  };

  const emailBodyEditorRef = useRef<RichTextEditorHandle>(null);

  const handleInsertEmailVariable = (variable: string) => {
    if (emailBodyEditorRef.current) {
      emailBodyEditorRef.current.insertAtCursor(variable);
    } else {
      setEmailBody((prev) => prev + variable);
    }
  };

  const handleSendEmail = () => {
    if (!emailRecipient || !emailSubject.trim()) {
      toast({ title: "Missing fields", description: "Please enter a recipient and subject.", variant: "destructive" });
      return;
    }
    sendEmailMutation.mutate({ 
      to: emailRecipient, 
      subject: emailSubject, 
      body: emailBody,
      clientId: selectedClientId || undefined,
      cc: emailCc.trim() || undefined,
      attachments: emailAttachments,
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedClientIds(new Set(sortedClients.map(c => c.id)));
    } else {
      setSelectedClientIds(new Set());
    }
  };

  const handleSelectClient = (clientId: string, checked: boolean) => {
    const newSet = new Set(selectedClientIds);
    if (checked) {
      newSet.add(clientId);
    } else {
      newSet.delete(clientId);
    }
    setSelectedClientIds(newSet);
  };

  const toggleSort = (field: string) => {
    // Server-side sort is the source of truth — silently ignore clicks
    // on columns the storage layer can't sort by.
    if (!SERVER_SORTABLE.has(field)) return;
    if (sortField === field) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        // Reset to backend default sort.
        setSortField(null);
        setSortDirection("desc");
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    // Hide the sort affordance entirely on non-sortable columns to
    // avoid the "click did nothing" feel.
    if (!SERVER_SORTABLE.has(field)) return null;
    if (sortField !== field) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    }
    return sortDirection === "asc"
      ? <ArrowUp className="w-3 h-3 ml-1" />
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  // Server is authoritative — no JS-side filter or sort. The flat list
  // already reflects regions + clientType + talent + q + sort.
  const sortedClients = clients;
  const visibleClients = sortedClients;
  const hasMoreClients = !!hasNextPage;

  const handleCellChange = (clientId: string, column: string, value: string) => {
    const client = clients.find(c => c.id === clientId);
    if (!client) return;
    
    const oldValue = (client as any)[column];
    
    updateClientMutation.mutate({ id: clientId, updates: { [column]: value } });
    
    setUndoStack(prev => [...prev, { type: "edit", clientId, field: column, oldValue, newValue: value }]);
    setRedoStack([]);
  };

  const handleDeleteWithUndo = (client: Client) => {
    // The undo stack is updated in the mutation's onSuccess callback
    deleteClientMutation.mutate(client);
  };

  const handleUndo = async () => {
    if (undoStack.length === 0) return;
    
    const action = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));
    
    try {
      if (action.type === "edit") {
        await apiRequest("PATCH", `/api/clients/${action.clientId}`, { [action.field]: action.oldValue });
        setRedoStack(prev => [...prev, action]);
        queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
        toast({ title: "Undone", description: "Edit has been reverted." });
      } else if (action.type === "delete") {
        const { id, createdAt, updatedAt, ...clientData } = action.client;
        const response = await apiRequest("POST", "/api/clients", clientData);
        const newClient = await response.json();
        setRedoStack(prev => [...prev, { type: "delete", client: { ...action.client, id: newClient.id } }]);
        queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
        toast({ title: "Undone", description: "Client has been restored." });
      } else if (action.type === "bulkDelete") {
        // Bulk delete is a soft delete — restore each record by ID.
        await Promise.all(
          action.clientIds.map(clientId => apiRequest("POST", `/api/clients/${clientId}/restore`)),
        );
        setRedoStack(prev => [...prev, action]);
        queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
        toast({ title: "Undone", description: `Restored ${action.clientIds.length} clients.` });
      }
    } catch {
      setUndoStack(prev => [...prev, action]);
      toast({ title: "Error", description: "Failed to undo action.", variant: "destructive" });
    }
  };

  const handleRedo = async () => {
    if (redoStack.length === 0) return;
    
    const action = redoStack[redoStack.length - 1];
    setRedoStack(prev => prev.slice(0, -1));
    
    try {
      if (action.type === "edit") {
        await apiRequest("PATCH", `/api/clients/${action.clientId}`, { [action.field]: action.newValue });
        setUndoStack(prev => [...prev, action]);
        queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
        toast({ title: "Redone", description: "Edit has been reapplied." });
      } else if (action.type === "delete") {
        await apiRequest("DELETE", `/api/clients/${action.client.id}`);
        const deletedClient = action.client;
        setUndoStack(prev => [...prev, { type: "delete", client: deletedClient }]);
        queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
        toast({ title: "Redone", description: "Client has been deleted again." });
      } else if (action.type === "bulkDelete") {
        // Re-run the bulk soft delete on the same IDs.
        await apiRequest("POST", "/api/clients/bulk-action", {
          action: "delete",
          scope: { type: "ids", ids: action.clientIds },
        });
        setUndoStack(prev => [...prev, action]);
        queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
        toast({ title: "Redone", description: `Deleted ${action.clientIds.length} clients again.` });
      }
    } catch {
      setRedoStack(prev => [...prev, action]);
      toast({ title: "Error", description: "Failed to redo action.", variant: "destructive" });
    }
  };

  const handleRowDoubleClick = (clientId: string) => {
    if (clickGuardRef.current) return;
    clickGuardRef.current = true;
    setTimeout(() => { clickGuardRef.current = false; }, 400);
    setSelectedClientId(clientId);
    setIsDialogOpen(true);
  };

  const handleRowClick = (clientId: string) => {
    setSelectedClientId(selectedClientId === clientId ? null : clientId);
  };

  const formatValue = (client: Client, key: string) => {
    const value = (client as any)[key];
    if (key === "amount") {
      const rate = parseFloat((client as any).rate || '0');
      const discount = parseFloat((client as any).discount || '0');
      const amount = Math.max(0, rate - discount);
      return amount > 0 ? `$${amount.toFixed(2)}` : "";
    }
    if (!value) return "";
    if (key === "dateOfEvent" || key === "dateBooked") {
      try {
        const s = String(value);
        if (s.includes("T") || s.includes("Z")) {
          // ISO timestamp (e.g. dateBooked) — parse as a real instant, show local date
          const dt = new Date(s);
          return isNaN(dt.getTime()) ? value : dt.toLocaleDateString("en-US");
        }
        // Calendar-day date string (YYYY-MM-DD) — parse without tz-shift
        const [y, mo, d] = s.split("-").map(Number);
        return new Date(y, mo - 1, d).toLocaleDateString("en-US");
      } catch {
        return value;
      }
    }
    if (key === "regionId") {
      const region = regions.find(r => r.id === value);
      return region?.name || "";
    }
    return value;
  };

  // Resolve the selected client. Prefer the already-loaded list row;
  // otherwise fetch the single record directly via GET /api/clients/:id.
  // This makes the post-conversion auto-open (and quick-find to a client
  // on a not-yet-loaded page) near-instant instead of blocking on the
  // cursor-paginated list query.
  const clientInList = clients.find(c => c.id === selectedClientId) ?? null;
  const { data: fetchedSelectedClient } = useQuery<Client>({
    queryKey: ["/api/clients", "single", selectedClientId],
    queryFn: async () => {
      const response = await fetch(`/api/clients/${selectedClientId}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch client");
      return response.json();
    },
    enabled: !!selectedClientId && !clientInList,
  });
  const selectedClient =
    clientInList ??
    (fetchedSelectedClient && fetchedSelectedClient.id === selectedClientId
      ? fetchedSelectedClient
      : null);

  // Activity count for the "Catch me up" gating in the mobile action sheet
  const { data: selectedClientActivities = [] } = useQuery<any[]>({
    queryKey: ["/api/clients", selectedClient?.id, "activities"],
    queryFn: async () => {
      if (!selectedClient?.id) return [];
      const response = await fetch(`/api/clients/${selectedClient.id}/activities`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch activities");
      return response.json();
    },
    enabled: isMobileActionSheetOpen && !!selectedClient?.id,
  });

  return (
    <div className="flex flex-col h-full">
      <SavedViewsTabStrip
        entity="clients"
        getCurrentParams={() => ({
          sort: sortField,
          dir: sortDirection,
          regions: selectedRegions,
          clientType: selectedClientType,
          talentId: selectedTalentId,
        })}
        applyParams={(p) => {
          if (typeof p.sort === "string" || p.sort === null) setSortField((p.sort as any) ?? null);
          if (p.dir === "asc" || p.dir === "desc") setSortDirection(p.dir);
          if (Array.isArray(p.regions)) setSelectedRegions(p.regions.filter((x): x is string => typeof x === "string"));
          if (typeof p.clientType === "string") setSelectedClientType(p.clientType);
          if (typeof p.talentId === "string") setSelectedTalentId(p.talentId);
        }}
        activeViewId={activeViewId}
        onActiveViewChange={setActiveViewId}
        rightSlot={savedViewsRightSlot}
      />
      {/* Desktop toolbar — replaced by a Filters sheet on mobile */}
      {!isMobile && (
      <div className="border-b border-border bg-card overflow-x-auto">
        <div className="flex items-center gap-3 p-4 min-w-max">
          <Label className="text-sm font-medium">Region:</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                className="w-64 justify-between" 
                data-testid="select-region-filter"
              >
                <span className="truncate">
                  {selectedRegions.length === 0 
                    ? "All Regions" 
                    : selectedRegions.length === 1 
                      ? regions.find(r => r.id === selectedRegions[0])?.name || "1 region"
                      : `${selectedRegions.length} regions selected`}
                </span>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[200px] p-0" align="start">
              <div className="p-2 border-b">
                <Input
                  placeholder="Search regions (e.g., OH)"
                  value={regionSearch}
                  onChange={(e) => setRegionSearch(e.target.value)}
                  className="h-8"
                  data-testid="input-region-filter-search"
                />
              </div>
              <div className="max-h-60 overflow-auto p-2 space-y-1">
                <div 
                  className="flex items-center space-x-2 p-2 rounded hover:bg-muted cursor-pointer"
                  onClick={() => setSelectedRegions([])}
                >
                  <Checkbox 
                    checked={selectedRegions.length === 0} 
                    onCheckedChange={() => setSelectedRegions([])}
                    onClick={(e) => e.stopPropagation()}
                    data-testid="checkbox-all-regions-clients"
                  />
                  <span className="text-sm font-medium">All Regions</span>
                </div>
                {filteredRegions.length === 0 && regionSearch.trim() ? (
                  <div className="py-2 px-2 text-sm text-muted-foreground text-center">
                    No regions match "{regionSearch}"
                  </div>
                ) : (
                  filteredRegions.map((region) => (
                    <div 
                      key={region.id}
                      className="flex items-center space-x-2 p-2 rounded hover:bg-muted cursor-pointer"
                      onClick={() => {
                        setSelectedRegions(prev => 
                          prev.includes(region.id) 
                            ? prev.filter(id => id !== region.id)
                            : [...prev, region.id]
                        );
                      }}
                    >
                      <Checkbox 
                        checked={selectedRegions.includes(region.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedRegions(prev => [...prev, region.id]);
                          } else {
                            setSelectedRegions(prev => prev.filter(id => id !== region.id));
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`checkbox-region-${region.id}`}
                      />
                      <span className="text-sm">{region.name}</span>
                    </div>
                  ))
                )}
              </div>
              {selectedRegions.length > 0 && (
                <div className="p-2 border-t">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full" 
                    onClick={() => setSelectedRegions([])}
                    data-testid="button-clear-regions-clients"
                  >
                    Clear Selection
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>

          {/* Client Type Filter */}
          <div className="flex items-center gap-2 ml-4 pl-4 border-l border-border">
            <Label className="text-sm font-medium">Client Type:</Label>
            <Select value={selectedClientType === "" || selectedClientType === "__unset__" ? "__all__" : selectedClientType} onValueChange={(v) => setSelectedClientType(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-48" data-testid="select-client-type-filter">
                <SelectValue placeholder="All Client Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Client Types</SelectItem>
                {clientTypeOptions.map((ct) => (
                  <SelectItem key={ct} value={ct}>{ct}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Talent Filter */}
          <div className="flex items-center gap-2 ml-2 pl-2 border-l border-border">
            <Label className="text-sm font-medium">Talent:</Label>
            <Select
              value={selectedTalentId === "__unset__" ? "" : selectedTalentId}
              onValueChange={(value) => {
                talentPrefExplicitRef.current = true;
                setSelectedTalentId(value);
              }}
            >
              <SelectTrigger className="w-48" data-testid="select-talent-filter">
                <SelectValue placeholder="All Talent" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Talent</SelectItem>
                {talent.filter(e => e.isActive).map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name} {e.isDefault && "(Default)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Undo/Redo buttons */}
          <div className="flex items-center gap-1 ml-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleUndo}
              disabled={undoStack.length === 0}
              className="h-11 w-11 sm:h-8 sm:w-8"
              title="Undo"
              data-testid="button-undo-clients"
            >
              <Undo2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRedo}
              disabled={redoStack.length === 0}
              className="h-11 w-11 sm:h-8 sm:w-8"
              title="Redo"
              data-testid="button-redo-clients"
            >
              <Redo2 className="w-4 h-4" />
            </Button>
          </div>
          
        </div>
      </div>
      )}
      
      {/* Row action toolbar — hidden on mobile (per-card action sheet handles these) */}
      {!isMobile && (
      <div className="flex items-center justify-between flex-wrap gap-2 px-4 py-2 border-b border-border bg-muted/50">
        {/* Left side: Email and Log Call */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (selectedClient?.emailAddress) {
                handleOpenEmailDialog(selectedClient.emailAddress);
              } else {
                toast({ title: "No Email", description: "This client doesn't have an email address.", variant: "destructive" });
              }
            }}
            disabled={!selectedClient?.emailAddress}
            data-testid="button-email-client"
          >
            <Mail className="w-4 h-4 mr-1" />
            Email
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsLogCallDialogOpen(true)}
            disabled={!selectedClientId}
            data-testid="button-log-call-client"
          >
            <Phone className="w-4 h-4 mr-1" />
            Log Call
          </Button>
        </div>

        {/* Right side: Set Reminder, Activity, Client Actions, Add Client */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsSetReminderDialogOpen(true)}
            disabled={!selectedClientId}
            data-testid="button-set-reminder-client"
          >
            <Bell className="w-4 h-4 mr-1" />
            Set Reminder
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsActivityDialogOpen(true)}
            disabled={!selectedClientId}
            data-testid="button-activity-client"
          >
            <Eye className="w-4 h-4 mr-1" />
            Activity
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={!selectedClientId} data-testid="dropdown-client-actions">
                Client Actions
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => setIsDialogOpen(true)}
                data-testid="action-edit-client"
              >
                <Pencil className="w-4 h-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setIsLogEventDialogOpen(true)}
                data-testid="action-book-show-client"
                onSelect={(e) => {
                  if ((e.target as HTMLElement)?.closest?.('[data-testid^="tutorial-hint-"]')) {
                    e.preventDefault();
                  }
                }}
              >
                <Calendar className="w-4 h-4 mr-2 text-primary" />
                <span className="flex-1">Book Event</span>
                <TutorialHint id="logShow.button" className="ml-2" />
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => selectedClient && setClientToDelete(selectedClient)}
                className="text-destructive focus:text-destructive"
                data-testid="action-delete-client"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button
            size="sm"
            onClick={() => setIsNewClientDialogOpen(true)}
            data-testid="button-add-client"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Client
          </Button>
        </div>
      </div>
      )}

      {/* Bulk Edit Toolbar */}
      {selectedClientIds.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-primary/10 border-b border-border flex-wrap">
          <span className="text-sm font-medium" data-testid="text-bulk-selection-count-clients">
            {selectAllMatching
              ? `All clients matching current filters selected`
              : `${selectedClientIds.size} selected`}
          </span>
          {!selectAllMatching && hasNextPage && (
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              onClick={() => setSelectAllMatching(true)}
              data-testid="button-select-all-matching-clients"
            >
              Select all clients matching current filters
            </Button>
          )}
          {canBulkActions && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsBulkEditOpen(true)}
              data-testid="button-bulk-edit-clients"
            >
              <Edit className="w-4 h-4 mr-1" />
              Bulk Edit
            </Button>
          )}
          {canBulkActions && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setIsBulkDeleteOpen(true)}
              data-testid="button-bulk-delete-clients"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete Selected
            </Button>
          )}
          {canBulkActions && selectedClientIds.size === 2 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                const ids = Array.from(selectedClientIds);
                const firstId = ids[0];
                setPrimaryClientId(firstId);
                setMergeStep(1);
                setMergeFieldSelections({ accountName: "primary", contactName: "primary", emailAddress: "primary", phoneNumber: "primary", mobilePhone: "primary", address: "primary", city: "primary", state: "primary", zipCode: "primary", notes: "primary" });
                const p = clients.find(c => c.id === firstId);
                setMergeEditedValues({ accountName: p?.accountName || "", contactName: p?.contactName || "", emailAddress: p?.emailAddress || "", phoneNumber: p?.phoneNumber || "", mobilePhone: p?.mobilePhone || "", address: p?.address || "", city: p?.city || "", state: p?.state || "", zipCode: p?.zipCode || "", notes: p?.notes || "" });
                setIsMergeDialogOpen(true);
              }}
              data-testid="button-merge-clients"
            >
              <GitMerge className="w-4 h-4 mr-1" />
              Merge Selected
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedClientIds(new Set());
              setSelectAllMatching(false);
            }}
            data-testid="button-clear-client-selection"
          >
            Clear Selection
          </Button>
        </div>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={isBulkDeleteOpen} onOpenChange={setIsBulkDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {selectedClientIds.size} Clients?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-4">
            The selected clients will be removed from your list. This can be reversed with the Undo button.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsBulkDeleteOpen(false)} data-testid="button-cancel-bulk-delete-clients">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={bulkDeleteMutation.isPending}
              data-testid="button-confirm-bulk-delete-clients"
            >
              {bulkDeleteMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete {selectedClientIds.size} Clients
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={isBulkEditOpen} onOpenChange={setIsBulkEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Edit {selectedClientIds.size} Clients</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Field to Update</Label>
              <Select value={bulkEditField} onValueChange={setBulkEditField}>
                <SelectTrigger data-testid="select-bulk-client-field">
                  <SelectValue placeholder="Select field" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="eventDetails">Booking</SelectItem>
                  <SelectItem value="regionId">Region</SelectItem>
                  <SelectItem value="talentId">Talent</SelectItem>
                  <SelectItem value="bookingDetails">Booking Details</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {bulkEditField === "talentId" && (
              <div className="space-y-2">
                <Label>New Talent</Label>
                <Select value={bulkEditValue} onValueChange={setBulkEditValue}>
                  <SelectTrigger data-testid="select-bulk-client-talent">
                    <SelectValue placeholder="Select talent" />
                  </SelectTrigger>
                  <SelectContent>
                    {talent.filter(e => e.isActive).map((e) => (
                      <SelectItem key={e.id} value={e.id} data-testid={`option-bulk-talent-${e.id}`}>
                        {e.name} {e.isDefault && "(Default)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {bulkEditField === "eventDetails" && (
              <div className="space-y-2">
                <Label>New Booking</Label>
                <Select value={bulkEditValue} onValueChange={setBulkEditValue}>
                  <SelectTrigger data-testid="select-bulk-show-details">
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent>
                    {SHOW_DETAILS_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {bulkEditField === "regionId" && (
              <div className="space-y-2">
                <Label>New Region</Label>
                <Select value={bulkEditValue} onValueChange={setBulkEditValue}>
                  <SelectTrigger data-testid="select-bulk-client-region">
                    <SelectValue placeholder="Select region" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientTypeFilteredRegions.map((region) => (
                      <SelectItem key={region.id} value={region.id}>
                        {region.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedClientType && selectedClientType !== "__unset__" && (
                  <p className="text-xs text-muted-foreground">
                    Showing regions tagged for "{selectedClientType}".
                  </p>
                )}
              </div>
            )}
            {bulkEditField === "bookingDetails" && (
              <div className="space-y-2">
                <Label>New Booking Details</Label>
                <Textarea
                  value={bulkEditValue}
                  onChange={(e) => setBulkEditValue(e.target.value)}
                  placeholder="Enter booking details..."
                  data-testid="input-bulk-booking-details"
                />
              </div>
            )}
            <Button
              onClick={handleBulkEdit}
              disabled={!bulkEditField || !bulkEditValue || bulkEditMutation.isPending}
              className="w-full"
              data-testid="button-apply-bulk-client-edit"
            >
              {bulkEditMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Apply to {selectedClientIds.size} Clients
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Merge Clients Dialog — multi-step wizard */}
      {(() => {
        const ids = Array.from(selectedClientIds);
        const secondaryClientId = ids.find(id => id !== primaryClientId) || ids[1] || "";
        const primaryClient = clients.find(c => c.id === primaryClientId);
        const secondaryClient = clients.find(c => c.id === secondaryClientId);

        const mergeFieldDefs: { key: MergeFieldKey; label: string; multiline?: boolean }[] = [
          { key: "accountName", label: "Account Name" },
          { key: "contactName", label: "Contact Name" },
          { key: "emailAddress", label: "Email" },
          { key: "phoneNumber", label: "Phone" },
          { key: "mobilePhone", label: "Mobile" },
          { key: "address", label: "Address" },
          { key: "city", label: "City" },
          { key: "state", label: "State" },
          { key: "zipCode", label: "Zip Code" },
          { key: "notes", label: "Notes", multiline: true },
        ];

        const getVal = (client: typeof primaryClient, key: MergeFieldKey) =>
          (client as any)?.[key] as string | null | undefined;

        const applySelection = (key: MergeFieldKey, side: "primary" | "secondary") => {
          const val = side === "primary" ? getVal(primaryClient, key) : getVal(secondaryClient, key);
          setMergeFieldSelections(prev => ({ ...prev, [key]: side }));
          setMergeEditedValues(prev => ({ ...prev, [key]: val || "" }));
        };

        const selectAllFrom = (side: "primary" | "secondary") => {
          const newSelections = { ...mergeFieldSelections };
          const newValues = { ...mergeEditedValues };
          for (const { key } of mergeFieldDefs) {
            newSelections[key] = side;
            const src = side === "primary" ? primaryClient : secondaryClient;
            newValues[key] = getVal(src, key) || "";
          }
          setMergeFieldSelections(newSelections);
          setMergeEditedValues(newValues);
        };

        const conflictingFields = mergeFieldDefs.filter(({ key }) => {
          const pv = getVal(primaryClient, key) || "";
          const sv = getVal(secondaryClient, key) || "";
          return pv !== sv && (pv || sv);
        });

        return (
          <Dialog open={isMergeDialogOpen} onOpenChange={(open) => {
            setIsMergeDialogOpen(open);
            if (!open) { setMergeStep(1); }
          }}>
            <DialogContent className="w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              {/* Step indicator */}
              <div className="flex items-center gap-2 mb-1">
                {[1, 2, 3].map((s) => (
                  <div key={s} className="flex items-center gap-2">
                    <div className={cn(
                      "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors",
                      mergeStep === s ? "bg-primary text-primary-foreground border-primary" :
                      mergeStep > s ? "bg-primary/20 text-primary border-primary/40" :
                      "bg-muted text-muted-foreground border-border"
                    )}>
                      {mergeStep > s ? <Check className="w-3.5 h-3.5" /> : s}
                    </div>
                    {s < 3 && <div className={cn("h-0.5 w-8", mergeStep > s ? "bg-primary/40" : "bg-border")} />}
                  </div>
                ))}
                <span className="ml-2 text-sm text-muted-foreground">
                  {mergeStep === 1 ? "Choose Primary Record" : mergeStep === 2 ? "Resolve Conflicts" : "Review & Confirm"}
                </span>
              </div>

              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <GitMerge className="w-5 h-5" />
                  Merge Clients
                </DialogTitle>
                <DialogDescription>
                  {mergeStep === 1 && "Select which client record to keep. All invoices will be transferred to the primary record."}
                  {mergeStep === 2 && "Choose which value to use for each conflicting field. Click a value to select it, then edit if needed."}
                  {mergeStep === 3 && "Review the final merged record before committing. You can edit any field."}
                </DialogDescription>
              </DialogHeader>

              {/* ── STEP 1: Pick primary ── */}
              {mergeStep === 1 && (
                <div className="space-y-3 py-2">
                  <p className="text-sm text-muted-foreground">Click the record you want to keep:</p>
                  {ids.map((id) => {
                    const cl = clients.find(c => c.id === id);
                    if (!cl) return null;
                    const isPrimary = id === primaryClientId;
                    const invCount = (invoicesData as any[])?.filter((inv: any) => inv.clientId === id).length || 0;
                    return (
                      <button
                        key={id}
                        onClick={() => {
                          setPrimaryClientId(id);
                          const p = clients.find(c => c.id === id);
                          setMergeEditedValues({ accountName: p?.accountName || "", contactName: p?.contactName || "", emailAddress: p?.emailAddress || "", phoneNumber: p?.phoneNumber || "", mobilePhone: p?.mobilePhone || "", address: p?.address || "", city: p?.city || "", state: p?.state || "", zipCode: p?.zipCode || "", notes: p?.notes || "" });
                          setMergeFieldSelections({ accountName: "primary", contactName: "primary", emailAddress: "primary", phoneNumber: "primary", mobilePhone: "primary", address: "primary", city: "primary", state: "primary", zipCode: "primary", notes: "primary" });
                        }}
                        className={cn(
                          "w-full text-left p-4 rounded-lg border-2 transition-all",
                          isPrimary ? "border-primary bg-primary/5" : "border-border bg-card hover:border-border/80"
                        )}
                        data-testid={`button-select-primary-${id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold">{cl.accountName}</p>
                            {cl.contactName && <p className="text-sm text-muted-foreground">{cl.contactName}</p>}
                            {cl.emailAddress && <p className="text-sm text-muted-foreground">{cl.emailAddress}</p>}
                            {cl.city && cl.state && <p className="text-sm text-muted-foreground">{cl.city}, {cl.state}</p>}
                            <p className="text-xs text-muted-foreground mt-1">{invCount} invoice{invCount !== 1 ? "s" : ""}</p>
                          </div>
                          <div className={cn(
                            "w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1",
                            isPrimary ? "border-primary bg-primary" : "border-muted-foreground"
                          )}>
                            {isPrimary && <Check className="w-3.5 h-3.5 text-primary-foreground" />}
                          </div>
                        </div>
                        {isPrimary && (
                          <p className="text-xs font-medium text-primary mt-2">✓ This record will be kept</p>
                        )}
                      </button>
                    );
                  })}
                  <div className="p-3 bg-muted/60 rounded-md text-xs text-muted-foreground space-y-1 mt-2">
                    <p>• The non-selected record will be permanently deleted after merging</p>
                    <p>• All invoices from both records will be moved to the kept record</p>
                    <p>• You'll resolve any field conflicts in the next step</p>
                  </div>
                </div>
              )}

              {/* ── STEP 2: Field-level conflict resolution ── */}
              {mergeStep === 2 && primaryClient && secondaryClient && (
                <div className="space-y-3 py-2">
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="text-center">
                      <button onClick={() => selectAllFrom("primary")} className="text-xs text-primary underline hover:no-underline">Use all primary values</button>
                    </div>
                    <div className="text-center">
                      <button onClick={() => selectAllFrom("secondary")} className="text-xs text-primary underline hover:no-underline">Use all secondary values</button>
                    </div>
                  </div>

                  {/* Column headers */}
                  <div className="grid grid-cols-[110px_1fr_1fr] gap-2 text-xs font-semibold text-muted-foreground px-1 pb-1 border-b">
                    <div />
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                      {primaryClient.accountName}
                      <span className="text-muted-foreground font-normal">(keep)</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-muted-foreground" />
                      {secondaryClient.accountName}
                      <span className="text-muted-foreground font-normal">(delete)</span>
                    </div>
                  </div>

                  <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
                    {mergeFieldDefs.map(({ key, label }) => {
                      const pv = getVal(primaryClient, key) || "";
                      const sv = getVal(secondaryClient, key) || "";
                      const isConflict = pv !== sv && (pv || sv);
                      const sel = mergeFieldSelections[key];

                      return (
                        <div key={key} className={cn(
                          "grid grid-cols-[110px_1fr_1fr] gap-2 items-start rounded-md px-1 py-1.5",
                          isConflict ? "bg-amber-50 dark:bg-amber-950/20" : "bg-transparent"
                        )}>
                          <div className="text-xs font-medium pt-1.5 text-muted-foreground flex items-center gap-1">
                            {isConflict && <span className="text-amber-500">⚠</span>}
                            {label}
                          </div>
                          {/* Primary value */}
                          <button
                            onClick={() => applySelection(key, "primary")}
                            className={cn(
                              "text-left text-xs px-2.5 py-1.5 rounded border transition-all",
                              sel === "primary"
                                ? "border-primary bg-primary/10 font-medium ring-1 ring-primary/30"
                                : "border-transparent bg-muted/50 hover:bg-muted",
                              !pv && "opacity-40 italic"
                            )}
                            data-testid={`button-select-primary-field-${key}`}
                          >
                            {pv || <span className="text-muted-foreground">—</span>}
                            {sel === "primary" && pv && <CheckCircle2 className="w-3 h-3 text-primary inline ml-1 align-middle" />}
                          </button>
                          {/* Secondary value */}
                          <button
                            onClick={() => applySelection(key, "secondary")}
                            className={cn(
                              "text-left text-xs px-2.5 py-1.5 rounded border transition-all",
                              sel === "secondary"
                                ? "border-primary bg-primary/10 font-medium ring-1 ring-primary/30"
                                : "border-transparent bg-muted/50 hover:bg-muted",
                              !sv && "opacity-40 italic"
                            )}
                            data-testid={`button-select-secondary-field-${key}`}
                          >
                            {sv || <span className="text-muted-foreground">—</span>}
                            {sel === "secondary" && sv && <CheckCircle2 className="w-3 h-3 text-primary inline ml-1 align-middle" />}
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {conflictingFields.length === 0 && (
                    <div className="text-center text-sm text-muted-foreground py-4">
                      No conflicting fields — both records have identical contact data.
                    </div>
                  )}
                </div>
              )}

              {/* ── STEP 3: Review & edit final record ── */}
              {mergeStep === 3 && (
                <div className="space-y-3 py-2">
                  <p className="text-sm text-muted-foreground mb-3">
                    Review and edit the final merged record. These values will be saved to the kept client.
                  </p>
                  <div className="space-y-3 max-h-[340px] overflow-y-auto pr-1">
                    {mergeFieldDefs.map(({ key, label, multiline }) => (
                      <div key={key} className="space-y-1">
                        <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
                        {multiline ? (
                          <Textarea
                            value={mergeEditedValues[key]}
                            onChange={e => setMergeEditedValues(prev => ({ ...prev, [key]: e.target.value }))}
                            rows={3}
                            className="text-sm"
                            data-testid={`input-merge-edit-${key}`}
                          />
                        ) : (
                          <Input
                            value={mergeEditedValues[key]}
                            onChange={e => setMergeEditedValues(prev => ({ ...prev, [key]: e.target.value }))}
                            className="text-sm"
                            data-testid={`input-merge-edit-${key}`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="p-3 bg-muted/60 rounded-md text-xs text-muted-foreground space-y-1 mt-2">
                    <p>• All invoices from both records will be kept under this record</p>
                    <p>• <strong>{secondaryClient?.accountName}</strong> will be permanently deleted after merging</p>
                  </div>
                </div>
              )}

              <DialogFooter className="flex-row justify-between gap-2 pt-2 border-t mt-2">
                <Button variant="outline" onClick={() => {
                  if (mergeStep === 1) setIsMergeDialogOpen(false);
                  else setMergeStep(prev => (prev - 1) as 1 | 2 | 3);
                }}>
                  {mergeStep === 1 ? "Cancel" : "Back"}
                </Button>
                <div className="flex gap-2">
                  {mergeStep < 3 && (
                    <Button
                      onClick={() => setMergeStep(prev => (prev + 1) as 1 | 2 | 3)}
                      disabled={!primaryClientId}
                      data-testid="button-merge-next"
                    >
                      Next <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                  {mergeStep === 3 && (
                    <Button
                      onClick={handleMergeClients}
                      disabled={mergeClientsMutation.isPending}
                      data-testid="button-confirm-merge"
                    >
                      {mergeClientsMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      <GitMerge className="w-4 h-4 mr-2" />
                      Merge Clients
                    </Button>
                  )}
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        );
      })()}

      {/* Status hint bar — surfaces what's happening with sort + search
          so users aren't confused by server-side semantics. Lives ABOVE
          the scroll container so it's visible across both mobile and
          desktop branches and never gets hidden inside the virtualized
          row area. */}
      {debouncedSearch.trim() && (
        <div className="px-3 py-1.5 border-b border-border bg-muted/30 text-xs text-muted-foreground space-y-0.5">
          {debouncedSearch.trim() && (
            <div data-testid="hint-search-scope">
              Searching name, contact, email, city, phone, notes &amp; address
            </div>
          )}
        </div>
      )}

      {/* Spreadsheet or Mobile Card List */}
      <div ref={scrollContainerRef} className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : clientsIsError ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3 text-muted-foreground" data-testid="state-clients-error">
            <p className="text-sm font-medium">Failed to load clients. Please try again.</p>
            <button
              className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={() => refetchClients()}
              data-testid="error-retry-button"
            >
              Retry
            </button>
          </div>
        ) : clients.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No clients yet"
            description="Clients appear here once you book an event from the Leads page."
          />
        ) : isMobile ? (
          <div className="p-3 space-y-2">
            {/* Mobile: Filters (Region / Client Type / Talent) */}
            <MobileFilterSheet
              activeCount={
                (selectedRegions.length > 0 ? 1 : 0) +
                (selectedClientType && selectedClientType !== "__unset__" ? 1 : 0) +
                (selectedTalentId && selectedTalentId !== "all" && selectedTalentId !== "__unset__" ? 1 : 0)
              }
              chips={[
                ...(selectedRegions.length > 0
                  ? [{
                      key: "regions",
                      label:
                        selectedRegions.length === 1
                          ? regions.find((r) => r.id === selectedRegions[0])?.name || "1 region"
                          : `${selectedRegions.length} regions`,
                      onRemove: () => setSelectedRegions([]),
                    }]
                  : []),
                ...(selectedClientType && selectedClientType !== "__unset__"
                  ? [{ key: "clientType", label: selectedClientType, onRemove: () => setSelectedClientType("") }]
                  : []),
                ...(selectedTalentId && selectedTalentId !== "all" && selectedTalentId !== "__unset__"
                  ? [{
                      key: "talent",
                      label: talent.find((t) => t.id === selectedTalentId)?.name || "Talent",
                      onRemove: () => setSelectedTalentId("all"),
                    }]
                  : []),
              ]}
              onClearAll={() => {
                setSelectedRegions([]);
                setSelectedClientType("");
                setSelectedTalentId("all");
              }}
            >
              <MobileFilterField label="Region">
                <MobileRegionPicker
                  regions={regions}
                  selected={selectedRegions}
                  onChange={setSelectedRegions}
                />
              </MobileFilterField>
              <MobileFilterField label="Client Type">
                <Select
                  value={selectedClientType === "" || selectedClientType === "__unset__" ? "__all__" : selectedClientType}
                  onValueChange={(v) => setSelectedClientType(v === "__all__" ? "" : v)}
                >
                  <SelectTrigger className="h-11" data-testid="select-mobile-client-type">
                    <SelectValue placeholder="All Client Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Client Types</SelectItem>
                    {clientTypeOptions.map((ct) => (
                      <SelectItem key={ct} value={ct}>{ct}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </MobileFilterField>
              <MobileFilterField label="Talent">
                <Select
                  value={selectedTalentId === "__unset__" ? "" : selectedTalentId}
                  onValueChange={(value) => {
                    talentPrefExplicitRef.current = true;
                    setSelectedTalentId(value);
                  }}
                >
                  <SelectTrigger className="h-11" data-testid="select-mobile-talent">
                    <SelectValue placeholder="All Talent" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Talent</SelectItem>
                    {talent.filter((e) => e.isActive).map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name} {e.isDefault && "(Default)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </MobileFilterField>
            </MobileFilterSheet>

            {/* Mobile: New Client button */}
            <Button
              className="w-full h-11 justify-center gap-2"
              onClick={() => setIsNewClientDialogOpen(true)}
              data-testid="button-mobile-add-client"
            >
              <Plus className="w-4 h-4" />
              Add New Client
            </Button>

            {visibleClients.map((client) => {
              const region = regions.find(r => r.id === client.regionId);
              const now = new Date();
              const showDate = client.dateOfEvent ? new Date(client.dateOfEvent) : null;
              const isPast = showDate && showDate < now;
              return (
                <div
                  key={client.id}
                  className="rounded-lg border border-border bg-card p-3 shadow-sm cursor-pointer active:bg-muted/50"
                  onClick={() => { setSelectedClientId(client.id); setIsMobileActionSheetOpen(true); }}
                  data-testid={`mobile-client-card-${client.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <span className="font-semibold text-sm leading-tight flex-1">{client.accountName || "Unnamed Client"}</span>
                    {client.dateOfEvent && (
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0 ${isPast ? "bg-muted text-muted-foreground" : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"}`}>
                        {showDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    )}
                  </div>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    {client.phoneNumber && (
                      <div className="flex items-center gap-1.5">
                        <Phone className="h-3 w-3 shrink-0" />
                        <a href={`tel:${client.phoneNumber}`} className="text-foreground" onClick={e => e.stopPropagation()}>{formatPhoneNumber(client.phoneNumber)}</a>
                      </div>
                    )}
                    {client.emailAddress && (
                      <div className="flex items-center gap-1.5">
                        <Mail className="h-3 w-3 shrink-0" />
                        <span className="truncate">{client.emailAddress}</span>
                        {invalidEmailSet.has((client.emailAddress || "").toLowerCase()) && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                data-testid={`badge-invalid-email-${client.id}`}
                                className="inline-block w-2 h-2 rounded-full bg-destructive shrink-0 cursor-default"
                              />
                            </TooltipTrigger>
                            <TooltipContent>Invalid email — failed verification. Edit to clear.</TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    )}
                    {(client.city || client.state) && (
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-3 w-3 shrink-0" />
                        <span>{[client.city, client.state].filter(Boolean).join(", ")}</span>
                      </div>
                    )}
                    {region && (
                      <div className="flex items-center gap-1.5">
                        <Eye className="h-3 w-3 shrink-0" />
                        <span>{region.name}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {hasMoreClients && (
              <div ref={loadMoreSentinelRef} className="h-10 flex items-center justify-center text-xs text-muted-foreground">
                {isFetchingNextPage ? "Loading more…" : ""}
              </div>
            )}
          </div>
        ) : (
          <div className="min-w-max">
            {/* Header */}
            <div className="flex sticky top-0 bg-muted border-b border-border z-10">
              <div className="w-8 shrink-0 border-r border-border flex items-center justify-center">
                <Checkbox
                  checked={sortedClients.length > 0 && sortedClients.every(c => selectedClientIds.has(c.id))}
                  onCheckedChange={handleSelectAll}
                  data-testid="checkbox-select-all-clients"
                />
              </div>
              {COLUMNS.map((col) => (
                <div
                  key={col.key}
                  draggable
                  onDragStart={(e) => {
                    setDraggedColKey(col.key);
                    e.dataTransfer.effectAllowed = "move";
                    try { e.dataTransfer.setData("text/plain", col.key); } catch {}
                  }}
                  onDragOver={(e) => {
                    if (!draggedColKey || draggedColKey === col.key) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (dragOverColKey !== col.key) setDragOverColKey(col.key);
                  }}
                  onDragLeave={() => {
                    if (dragOverColKey === col.key) setDragOverColKey(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const src = draggedColKey || e.dataTransfer.getData("text/plain");
                    if (src && src !== col.key) moveColumn(src, col.key);
                    setDraggedColKey(null);
                    setDragOverColKey(null);
                  }}
                  onDragEnd={() => {
                    setDraggedColKey(null);
                    setDragOverColKey(null);
                  }}
                  className={cn(
                    "spreadsheet-cell font-medium text-sm bg-muted relative group cursor-grab active:cursor-grabbing",
                    draggedColKey === col.key && "opacity-40",
                    dragOverColKey === col.key && draggedColKey !== col.key && "border-l-2 border-l-primary"
                  )}
                  style={{ width: getColumnWidth(col.key), minWidth: 50 }}
                  data-testid={`column-header-${col.key}`}
                >
                  <button
                    onClick={() => toggleSort(col.key)}
                    className="flex items-center hover:text-foreground w-full text-left"
                    data-testid={`sort-${col.key}`}
                  >
                    <span className="truncate">{col.label}</span>
                    <SortIcon field={col.key} />
                  </button>
                  <div
                    className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary/50 group-hover:bg-border"
                    onMouseDown={(e) => handleResizeStart(e, col.key)}
                  />
                </div>
              ))}
            </div>

            {/* Rows — progressive render (Fix B) */}
            {/* Rows — virtualized via @tanstack/react-virtual. Only the
                rows in (or near) the viewport actually mount, so
                scrolling stays smooth even with thousands of loaded
                clients. The outer absolutely-positioned wrapper holds
                the full virtual height so the scrollbar reflects the
                real row count. Pages stream in via fetchNextPage as the
                user approaches the bottom. */}
            <div
              style={{
                height: rowVirtualizer.getTotalSize(),
                width: "100%",
                position: "relative",
              }}
            >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const client = visibleClients[virtualRow.index];
              if (!client) return null;
              return (
              <div
                key={client.id}
                data-index={virtualRow.index}
                className={cn(
                  "flex border-b border-border hover-elevate cursor-pointer transition-colors",
                  selectedClientId === client.id && "spreadsheet-row-selected",
                  selectedClientIds.has(client.id) && "bg-primary/10"
                )}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${virtualRow.start}px)`,
                }}
                onClick={() => handleRowClick(client.id)}
                onDoubleClick={() => handleRowDoubleClick(client.id)}
              >
                <div className="w-8 shrink-0 h-7 flex items-center justify-center text-xs text-muted-foreground border-r border-border bg-muted/50">
                  <Checkbox
                    checked={selectedClientIds.has(client.id)}
                    onCheckedChange={(checked) => handleSelectClient(client.id, !!checked)}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`checkbox-client-${client.id}`}
                  />
                </div>
                {COLUMNS.map((col) => (
                  <div key={col.key} style={{ width: getColumnWidth(col.key), minWidth: 50 }}>
                    {col.type === "user-select" ? (
                      <Select
                        value={(client as any)[col.key] || ""}
                        onValueChange={(value) => handleCellChange(client.id, col.key, value)}
                      >
                        <SelectTrigger 
                          className="h-7 border-0 rounded-none text-sm focus:ring-0 focus:ring-offset-0"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`select-${col.key}-${client.id}`}
                        >
                          <SelectValue placeholder="Select rep...">
                            {(client as any)[col.key] || "Select rep..."}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {users.map((u) => (
                            <SelectItem key={u.id} value={`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || u.id}>
                              {`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || "Unknown"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : col.type === "region" && canEditRegion ? (
                      (() => {
                        const allowed = regionsForClientType(client.clientType);
                        const optionRegions = client.regionId && !allowed.find(r => r.id === client.regionId)
                          ? [...allowed, ...regions.filter(r => r.id === client.regionId)]
                          : allowed;
                        return (
                          <InlineRegionSelect
                            regions={optionRegions}
                            value={client.regionId || ""}
                            onChange={(value) => handleCellChange(client.id, "regionId", value)}
                            clientId={client.id}
                          />
                        );
                      })()
                    ) : (
                      <div 
                        className="spreadsheet-cell h-7 flex items-center px-2 text-sm truncate gap-1.5"
                        data-testid={`cell-${col.key}-${client.id}`}
                      >
                        {col.key === "accountName" && (client as any).requiresDocuments && (() => {
                          const docStatus = clientDocStatusMap.get(client.id);
                          const color = docStatus === "completed" ? "#22c55e" : docStatus === "pending" ? "#3b82f6" : "#eab308";
                          const label = docStatus === "completed" ? "Documents Completed" : docStatus === "pending" ? "Forms Pending" : "Send Forms";
                          return (
                            <span
                              title={label}
                              data-testid={`doc-status-${client.id}`}
                              style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: color, display: "inline-block", flexShrink: 0 }}
                            />
                          );
                        })()}
                        <span className="truncate flex-1">
                          {col.type === "region" 
                            ? regions.find(r => r.id === client.regionId)?.name || ""
                            : col.type === "talent"
                            ? talent.find(e => e.id === client.talentId)?.name || ""
                            : formatValue(client, col.key)
                          }
                        </span>
                        {col.key === "emailAddress" &&
                          client.emailAddress &&
                          invalidEmailSet.has((client.emailAddress || "").toLowerCase()) && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  data-testid={`badge-invalid-email-${client.id}`}
                                  className="inline-block w-2 h-2 rounded-full bg-destructive shrink-0 cursor-default"
                                />
                              </TooltipTrigger>
                              <TooltipContent>Invalid email — failed verification. Edit to clear.</TooltipContent>
                            </Tooltip>
                          )}
                        {col.type === "phone" && (client as any)[col.key] && twilioEnabled && (
                          <button
                            type="button"
                            aria-label="Call this number"
                            title="Call"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.dispatchEvent(
                                new CustomEvent("entertaincrm:dialer:open", {
                                  detail: {
                                    phone: String((client as any)[col.key]),
                                    contactName: (client as any).contactName || (client as any).accountName || null,
                                    clientId: client.id,
                                  },
                                }),
                              );
                            }}
                            className="opacity-60 hover:opacity-100 hover:text-primary shrink-0 p-0.5 rounded"
                            data-testid={`button-cell-dial-${client.id}`}
                          >
                            <Phone className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              );
            })}
            </div>

            {/* Infinite scroll sentinel — IntersectionObserver in the
                effect above watches this and calls fetchNextPage when
                it scrolls into view. */}
            {hasMoreClients && (
              <div ref={loadMoreSentinelRef} className="h-8 flex items-center justify-center text-xs text-muted-foreground">
                {isFetchingNextPage ? "Loading more clients…" : ""}
              </div>
            )}
            {!hasMoreClients && sortedClients.length > 0 && (
              <div className="h-6 flex items-center justify-center text-xs text-muted-foreground bg-muted/20 border-b border-border">
                Showing all {sortedClients.length} clients
              </div>
            )}
          </div>
        )}
      </div>

      {/* Client Record Dialog */}
      {selectedClient && (
        <ClientRecordDialog
          client={selectedClient}
          regions={regions}
          isOpen={isDialogOpen}
          onClose={() => {
            setIsDialogOpen(false);
            setSelectedClientId(null);
          }}
          onDuplicate={(newClient) => {
            setSelectedClientId(newClient.id);
            setIsDialogOpen(true);
          }}
          userId={userId}
        />
      )}

      {/* Client Activity Dialog */}
      <ClientActivityDialog
        client={selectedClient}
        isOpen={isActivityDialogOpen}
        onClose={() => {
          setIsActivityDialogOpen(false);
        }}
      />

      {/* Delete Client Confirmation Dialog */}
      <AlertDialog open={!!clientToDelete} onOpenChange={(open) => !open && setClientToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {clientToDelete?.accountName || "this client"}? You can undo this action using the Undo button.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-client">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (clientToDelete) {
                  handleDeleteWithUndo(clientToDelete);
                  setClientToDelete(null);
                }
              }}
              disabled={deleteClientMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-client"
            >
              {deleteClientMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Yes, Delete Client
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Dialog */}
      <Dialog open={isEmailDialogOpen} onOpenChange={(open) => {
        setIsEmailDialogOpen(open);
        if (!open) setEmailAttachments([]);
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Send Email</DialogTitle>
            <DialogDescription>
              Compose an email to {selectedClient?.accountName || "this client"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 flex-1 overflow-y-auto pr-2">
            <div className="grid gap-2">
              <Label htmlFor="email-to">To</Label>
              <Input
                id="email-to"
                type="email"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                placeholder="Enter recipient email..."
                data-testid="input-email-to"
              />
            </div>
            <CcEmailInput value={emailCc} onChange={setEmailCc} data-testid="input-email-cc" />
            <div className="grid gap-2">
              <Label htmlFor="template-select">Template</Label>
              <Select
                value={selectedTemplateId}
                onValueChange={(value) => {
                  setSelectedTemplateId(value);
                  if (value && value !== "none") {
                    const template = emailTemplates.find(t => t.id === value);
                    if (template) {
                      setEmailSubject(template.subject || "");
                      setEmailBody(template.bodyRich || template.body || "");
                    }
                  }
                }}
              >
                <SelectTrigger data-testid="select-email-template">
                  <SelectValue placeholder="Select a template (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No template</SelectItem>
                  {/* Task #696: filter picker-hidden templates. */}
                  {emailTemplates.filter((t) => !t.hiddenFromPickers).map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email-subject">Subject</Label>
              <Input
                id="email-subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Enter email subject..."
                data-testid="input-email-subject"
              />
            </div>
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label>Message</Label>
                <div className="flex items-center gap-2">
                  <VoiceToTextButton onTranscript={(t) => emailBodyEditorRef.current?.insertAtCursor(t)} />
                  <EmailVariablesSelector onInsert={handleInsertEmailVariable} />
                </div>
              </div>
              <RichTextEditor
                ref={emailBodyEditorRef}
                value={emailBody}
                onChange={setEmailBody}
                placeholder="Compose your message..."
                className="min-h-[200px] max-h-[300px] overflow-y-auto"
              />
            </div>
            <EmailAttachmentsField
              value={emailAttachments}
              onChange={setEmailAttachments}
              testIdPrefix="client-email-attachments"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEmailDialogOpen(false)} data-testid="button-cancel-email">
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsClientEmailScheduleOpen(true)}
              disabled={!emailRecipient || !emailSubject.trim() || sendEmailMutation.isPending}
              data-testid="button-schedule-client-email"
            >
              <Clock className="w-4 h-4 mr-2" />
              Schedule send
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={!emailRecipient || !emailSubject.trim() || sendEmailMutation.isPending}
              data-testid="button-send-email"
            >
              {sendEmailMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ScheduledEmailDialog
        open={isClientEmailScheduleOpen}
        onOpenChange={setIsClientEmailScheduleOpen}
        sourceType="one_off"
        recipientSummary={emailRecipient ? `Schedule email to ${emailRecipient}` : undefined}
        subjectSummary={emailSubject}
        buildPayload={() => {
          if (!emailRecipient || !emailSubject.trim() || !emailBody) return null;
          return {
            to: emailRecipient,
            cc: emailCc.trim() || undefined,
            subject: emailSubject,
            htmlBody: emailBody,
            attachments: emailAttachments.length > 0
              ? emailAttachments.map((a) => ({
                  filename: a.filename,
                  contentBase64: a.contentBase64,
                  contentType: a.contentType,
                }))
              : undefined,
            clientId: selectedClient?.id,
          };
        }}
        onScheduled={() => setIsEmailDialogOpen(false)}
      />

      {/* Log Call Dialog */}
      <LogCallDialog
        open={isLogCallDialogOpen}
        onOpenChange={setIsLogCallDialogOpen}
        title={`Log Call — ${selectedClient?.accountName || "Client"}`}
        onSave={(notes) => {
          if (selectedClientId) {
            logCallMutation.mutate({ clientId: selectedClientId, notes });
          }
        }}
        isPending={logCallMutation.isPending}
      />

      {/* Set Reminder Dialog */}
      <Dialog open={isSetReminderDialogOpen} onOpenChange={setIsSetReminderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Reminder</DialogTitle>
            <DialogDescription>
              Set a reminder for {selectedClient?.accountName || "this client"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="reminder-date">Reminder Date</Label>
              <Input
                id="reminder-date"
                type="date"
                value={reminderDate}
                onChange={(e) => setReminderDate(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                data-testid="input-reminder-date"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="reminder-note">Note</Label>
              <Textarea
                id="reminder-note"
                value={reminderNote}
                onChange={(e) => setReminderNote(e.target.value)}
                placeholder="What do you want to be reminded about?"
                rows={3}
                data-testid="input-reminder-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSetReminderDialogOpen(false)} data-testid="button-cancel-reminder">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedClientId && reminderDate) {
                  setReminderMutation.mutate({ clientId: selectedClientId, date: reminderDate, note: reminderNote });
                }
              }}
              disabled={!reminderDate || setReminderMutation.isPending}
              data-testid="button-save-reminder"
            >
              {setReminderMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Set Reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Event Dialog (Book Show) */}
      <LogEventDialog
        open={isLogEventDialogOpen}
        onOpenChange={(open) => setIsLogEventDialogOpen(open)}
        preselectedClient={selectedClient || null}
      />

      {/* Hidden button for triggering new client dialog from parent */}
      <button
        data-testid="button-spreadsheet-new-client"
        className="hidden"
        onClick={() => setIsNewClientDialogOpen(true)}
      />

      {/* New Client Dialog */}
      <Dialog open={isNewClientDialogOpen} onOpenChange={setIsNewClientDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Client</DialogTitle>
            <DialogDescription>
              Enter the basic information to create a new client record.
            </DialogDescription>
          </DialogHeader>
          <NewClientForm
            regions={regions}
            clientTypes={clientTypes}
            talent={talent}
            userId={userId}
            onSuccess={(newClient) => {
              setIsNewClientDialogOpen(false);
              setSelectedClientId(newClient.id);
              setIsDialogOpen(true);
            }}
            onCancel={() => setIsNewClientDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Mobile action sheet — opens when a client card is tapped */}
      <Sheet open={isMobileActionSheetOpen} onOpenChange={setIsMobileActionSheetOpen}>
        <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-xl pb-safe">
          <SheetHeader className="pb-2 border-b">
            <div className="flex items-center justify-between gap-2">
              <SheetTitle className="text-base">
                {selectedClient?.accountName || "Client Actions"}
              </SheetTitle>
              {selectedClient?.id && (
                <CatchMeUpButton
                  entityType="client"
                  entityId={selectedClient.id}
                  eventCount={selectedClientActivities.length}
                />
              )}
            </div>
          </SheetHeader>
          <div className="py-3 space-y-1">
            {/* Client info summary */}
            {selectedClient && (
              <div className="px-1 pb-3 mb-1 border-b space-y-1 text-sm text-muted-foreground">
                {selectedClient.phoneNumber && (
                  <div className="flex items-center gap-1">
                    <span className="font-medium text-foreground">Phone:</span>
                    <a href={`tel:${selectedClient.phoneNumber}`} className="text-primary" onClick={() => setIsMobileActionSheetOpen(false)}>{formatPhoneNumber(selectedClient.phoneNumber)}</a>
                  </div>
                )}
                {selectedClient.emailAddress && <div className="truncate"><span className="font-medium text-foreground">Email:</span> {selectedClient.emailAddress}</div>}
                {selectedClient.dateOfEvent && <div><span className="font-medium text-foreground">Event Date:</span> {new Date(selectedClient.dateOfEvent).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>}
              </div>
            )}

            {/* View full record */}
            <Button
              variant="ghost"
              className="w-full justify-start h-12 text-base gap-3"
              onClick={() => { setIsMobileActionSheetOpen(false); setIsDialogOpen(true); }}
              data-testid="mobile-client-action-view-record"
            >
              <Eye className="h-5 w-5 text-muted-foreground" /> View / Edit Record
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-12 text-base gap-3"
              onClick={() => { if (selectedClient?.emailAddress) handleOpenEmailDialog(selectedClient.emailAddress); setIsMobileActionSheetOpen(false); }}
              disabled={!selectedClient?.emailAddress}
              data-testid="mobile-client-action-email"
            >
              <Mail className="h-5 w-5 text-muted-foreground" /> Email Client
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-12 text-base gap-3"
              onClick={() => { setIsMobileActionSheetOpen(false); setIsLogCallDialogOpen(true); }}
              data-testid="mobile-client-action-log-call"
            >
              <Phone className="h-5 w-5 text-muted-foreground" /> Log Call
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-12 text-base gap-3"
              onClick={() => { setIsMobileActionSheetOpen(false); setIsSetReminderDialogOpen(true); }}
              data-testid="mobile-client-action-set-reminder"
            >
              <Bell className="h-5 w-5 text-muted-foreground" /> Set Reminder
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-12 text-base gap-3"
              onClick={() => { setIsMobileActionSheetOpen(false); setIsActivityDialogOpen(true); }}
              data-testid="mobile-client-action-view-activity"
            >
              <Eye className="h-5 w-5 text-muted-foreground" /> View Activity
            </Button>

            {/* Delete */}
            <div className="border-t pt-2 mt-1">
              <Button
                variant="ghost"
                className="w-full justify-start h-11 gap-3 text-destructive hover:text-destructive"
                onClick={() => { if (selectedClient) setClientToDelete(selectedClient); setIsMobileActionSheetOpen(false); }}
                data-testid="mobile-client-action-delete"
              >
                <Trash2 className="h-4 w-4" /> Delete Client
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

    </div>
  );
}

function NewClientForm({
  regions,
  clientTypes,
  talent,
  userId,
  onSuccess,
  onCancel,
}: {
  regions: Region[];
  clientTypes: ClientTypeRecord[];
  talent: Talent[];
  userId: string;
  onSuccess: (client: Client) => void;
  onCancel: () => void;
}) {
  const { toast } = useToast();
  const queryClientHook = useQueryClient();
  const [formData, setFormData] = useState({
    accountName: "",
    contactName: "",
    emailAddress: "",
    phoneNumber: "",
    mobilePhone: "",
    regionId: regions.length > 0 ? regions[0].id : "",
    clientType: clientTypes.find(ct => ct.isDefault)?.name || clientTypes[0]?.name || "",
    talentId: talent.find(e => e.isDefault)?.id || talent[0]?.id || null,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/clients", {
        ...formData,
        rep: userId,
      });
      return response.json();
    },
    onSuccess: (newClient) => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
      toast({ title: "Client Created", description: `${formData.accountName} has been created.` });
      onSuccess(newClient);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create client.", variant: "destructive" });
    },
  });

  return (
    <div className="space-y-4">
      <div className="grid gap-2">
        <Label htmlFor="accountName">Account Name *</Label>
        <Input
          id="accountName"
          value={formData.accountName}
          onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
          placeholder="Company or organization name"
          data-testid="input-new-client-account-name"
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="contactName">Contact Name</Label>
        <Input
          id="contactName"
          value={formData.contactName}
          onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
          placeholder="Primary contact person"
          data-testid="input-new-client-contact-name"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label htmlFor="emailAddress">Email</Label>
          <Input
            id="emailAddress"
            type="email"
            value={formData.emailAddress}
            onChange={(e) => setFormData({ ...formData, emailAddress: e.target.value })}
            placeholder="email@example.com"
            data-testid="input-new-client-email"
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="phoneNumber">Phone</Label>
          <Input
            id="phoneNumber"
            value={formData.phoneNumber}
            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
            placeholder="(555) 123-4567"
            data-testid="input-new-client-phone"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label htmlFor="mobilePhone">Mobile Phone</Label>
          <Input
            id="mobilePhone"
            value={formData.mobilePhone}
            onChange={(e) => setFormData({ ...formData, mobilePhone: e.target.value })}
            placeholder="(555) 123-4567"
            data-testid="input-new-client-mobile"
          />
        </div>
        <div></div>
      </div>

      {(() => {
        // T105: narrow region picker to those tagged for the selected client type.
        // (clientTypeIds empty/null on a region = unrestricted, so it events for all types.)
        const ctId = clientTypes.find((ct) => ct.name === formData.clientType)?.id;
        const allowedRegions = ctId
          ? regions.filter((r) => {
              const ids = (r as any).clientTypeIds as string[] | null | undefined;
              return !ids || ids.length === 0 || ids.includes(ctId);
            })
          : regions;
        // Always include the currently-selected region even if it's no longer allowed
        const visibleRegions = formData.regionId && !allowedRegions.find((r) => r.id === formData.regionId)
          ? [...allowedRegions, ...regions.filter((r) => r.id === formData.regionId)]
          : allowedRegions;
        return (
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="region">Region</Label>
              <SearchableRegionSelect
                regions={visibleRegions}
                value={formData.regionId}
                onChange={(value) => setFormData({ ...formData, regionId: value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="clientType">Client Type</Label>
              <Select
                value={formData.clientType}
                onValueChange={(value) => {
                  // If the currently selected region isn't tagged for the new client type, reset it
                  const newCtId = clientTypes.find((ct) => ct.name === value)?.id;
                  let nextRegionId = formData.regionId;
                  if (newCtId && nextRegionId) {
                    const currentRegion = regions.find((r) => r.id === nextRegionId);
                    const ids = (currentRegion as any)?.clientTypeIds as string[] | null | undefined;
                    const stillAllowed = !ids || ids.length === 0 || ids.includes(newCtId);
                    if (!stillAllowed) {
                      const firstAllowed = regions.find((r) => {
                        const rIds = (r as any).clientTypeIds as string[] | null | undefined;
                        return !rIds || rIds.length === 0 || rIds.includes(newCtId);
                      });
                      nextRegionId = firstAllowed?.id || "";
                    }
                  }
                  setFormData({ ...formData, clientType: value, regionId: nextRegionId });
                }}
              >
                <SelectTrigger data-testid="select-new-client-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {clientTypes.filter((ct) => ct.isActive).map((ct) => (
                    <SelectItem key={ct.id} value={ct.name}>{ct.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      })()}

      {talent.length > 1 && (
        <div className="grid gap-2">
          <Label htmlFor="talent">Talent</Label>
          <Select
            value={formData.talentId || ""}
            onValueChange={(value) => setFormData({ ...formData, talentId: value || null })}
          >
            <SelectTrigger data-testid="select-new-client-talent">
              <SelectValue placeholder="Select talent" />
            </SelectTrigger>
            <SelectContent>
              {talent.filter(e => e.isActive).map((ent) => (
                <SelectItem key={ent.id} value={ent.id}>{ent.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <DialogFooter className="pt-4">
        <Button variant="outline" onClick={onCancel} data-testid="button-cancel-new-client">
          Cancel
        </Button>
        <Button
          onClick={() => createMutation.mutate()}
          disabled={!formData.accountName.trim() || createMutation.isPending}
          data-testid="button-create-new-client"
        >
          {createMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Create Client
        </Button>
      </DialogFooter>
    </div>
  );
}

function SearchableRegionSelect({
  regions,
  value,
  onChange,
}: {
  regions: Region[];
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selectedRegion = regions.find((r) => r.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid="select-new-client-region"
        >
          {selectedRegion?.name || "Select region..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search regions..." />
          <CommandList>
            <CommandEmpty>No region found.</CommandEmpty>
            <CommandGroup>
              {regions.map((region) => (
                <CommandItem
                  key={region.id}
                  value={region.name}
                  onSelect={() => {
                    onChange(region.id);
                    setOpen(false);
                  }}
                  data-testid={`option-region-${region.id}`}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === region.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {region.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function InlineRegionSelect({
  regions,
  value,
  onChange,
  clientId,
}: {
  regions: Region[];
  value: string;
  onChange: (value: string) => void;
  clientId: string;
}) {
  const [open, setOpen] = useState(false);
  const selectedRegion = regions.find((r) => r.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="spreadsheet-cell h-7 w-full flex items-center px-2 text-sm truncate text-left hover:bg-muted/50 cursor-pointer"
          onClick={(e) => e.stopPropagation()}
          data-testid={`cell-regionId-${clientId}`}
        >
          {selectedRegion?.name || <span className="text-muted-foreground">Select...</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search regions..." />
          <CommandList>
            <CommandEmpty>No region found.</CommandEmpty>
            <CommandGroup>
              {regions.map((region) => (
                <CommandItem
                  key={region.id}
                  value={region.name}
                  onSelect={() => {
                    onChange(region.id);
                    setOpen(false);
                  }}
                  data-testid={`option-inline-region-${region.id}`}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === region.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {region.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
